import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import moment from 'moment-timezone';
import styled, { css } from 'styled-components';
import Button from '@material-ui/core/Button';
import { _ as agGridUtils } from 'ag-grid-community';
import type { ColDef, ColGroupDef } from 'ag-grid-community';

import { ReactDatePicker } from '@mummssoftware/common-ui';
import type { PatientChatQuestionRange } from '@mummssoftware/utils';

import { questionRange } from '../../utils/dateRange';
import defaults from '../../translations/en.json';
import { CalendarLayer } from './StartDatePopover';

const DATE_FORMAT = 'MM-dd-yy';
/**
 * Room for "MM-dd-yy". The picker's default (6.75rem) puts From and To wider
 * than the patient history's Messages, Duration and Tokens (265px).
 */
const DATE_INPUT_WIDTH = '4.75rem';

const QUESTION_DATE_FILTER_CLASS = 'maia-date-filter-group';
/** Group header height: legend over one row (MAIA Chats) or two (patient history). */
export const QUESTION_DATE_FILTER_HEIGHT = 50;
export const QUESTION_DATE_FILTER_STACKED_HEIGHT = 78;

/** The days a filter applies, as picked (the site's days, To included). */
export type QuestionDays = { from: Date; to: Date };

export type QuestionDateFilterState = {
  from: Date | null;
  to: Date | null;
  error: string | null;
  /** The days applied on Submit; null = every date. */
  days: QuestionDays | null;
  /** `days` for the gateway (site zone, To exclusive); null = every date. */
  range: PatientChatQuestionRange | null;
  pickFrom: (date: Date | null) => void;
  pickTo: (date: Date | null) => void;
  submit: () => void;
  clear: () => void;
};

/**
 * The date filter's state, kept by the widget rather than the filter: AG Grid
 * draws the filter in a header it may rebuild, which would drop picked dates.
 * The dates apply on Submit, so nothing reloads on every pick. Clear empties
 * both and goes back to every date.
 */
export const useQuestionDateFilter = (
  siteTimezone?: string | null,
): QuestionDateFilterState => {
  const [from, setFrom] = useState<Date | null>(null);
  const [to, setTo] = useState<Date | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [days, setDays] = useState<QuestionDays | null>(null);

  const range = useMemo(
    () => (days ? questionRange(days.from, days.to, siteTimezone) : null),
    [days, siteTimezone],
  );

  const pickFrom = useCallback((date: Date | null) => {
    setFrom(date);
    setError(null);
  }, []);
  const pickTo = useCallback((date: Date | null) => {
    setTo(date);
    setError(null);
  }, []);

  const submit = () => {
    if (!from && !to) {
      setDays(null);
    } else if (!from || !to) {
      setError(defaults.dateFilterIncomplete);
    } else if (moment(from).isAfter(to, 'day')) {
      // typed input can bypass the calendar's min/max
      setError(defaults.dateFilterOrder);
    } else {
      setDays({ from, to });
    }
  };

  const clear = () => {
    setFrom(null);
    setTo(null);
    setError(null);
    setDays(null);
  };

  return { from, to, error, days, range, pickFrom, pickTo, submit, clear };
};

/**
 * At least its group's width, never narrower than its own content: over
 * narrow columns it grows left, over the columns before it.
 */
const Frame = styled.fieldset<{ $stacked?: boolean; $error?: boolean }>`
  box-sizing: border-box;
  flex: none;
  width: max-content;
  height: 100%;
  min-width: 100%;
  margin: 0;
  padding: 0 8px 4px;
  border: 1px solid
    ${(props) =>
      props.$error
        ? props.theme.palette.error.main
        : props.theme.palette.primary.main};
  border-radius: 4px;
  display: flex;
  flex-direction: ${(props) => (props.$stacked ? 'column' : 'row')};
  align-items: center;
  justify-content: center;
  gap: ${(props) => (props.$stacked ? '4px' : '10px')};
  color: ${(props) => props.theme.palette.text.primary};
`;

/** The frame's caption, or the error in its place. */
const Legend = styled.legend<{ $error?: boolean }>`
  max-width: 100%;
  padding: 0 4px;
  overflow: hidden;
  font-size: 11px;
  line-height: 1.2;
  white-space: nowrap;
  text-overflow: ellipsis;
  color: ${(props) =>
    props.$error
      ? props.theme.palette.error.main
      : props.theme.palette.primary.main};
`;

const Row = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;

const Label = styled.span`
  font-size: 0.9rem;
`;

/** A little shorter than MUI's small button (30px). */
const FilterButton = styled(Button)`
  && {
    height: 24px;
    padding: 0 10px;
    line-height: 1;
  }
`;

/** Clear in the theme's red, like Stop in the chat. */
const ClearButton = styled(FilterButton)`
  && {
    background-color: ${(props) => props.theme.palette.error.main};
    color: ${(props) => props.theme.palette.error.contrastText};
    &:hover {
      background-color: ${(props) => props.theme.palette.error.dark};
    }
  }
`;

type QuestionDateFilterProps = {
  filter: QuestionDateFilterState;
  /** Buttons under the dates, for the narrower patient history columns. */
  stacked?: boolean;
};

/**
 * From, To, Submit and Clear in a frame captioned "Question dates", drawn over
 * the columns the dates recount (MAIA Chats and the patient history, expanded
 * only), so it is clear which numbers change. The error replaces the caption:
 * a header has no room for another line.
 */
export const QuestionDateFilter = ({
  filter,
  stacked,
}: QuestionDateFilterProps) => {
  const frameRef = useRef<HTMLFieldSetElement>(null);
  const today = moment().endOf('day').toDate();

  // In a grid header, AG Grid takes the arrow keys and Tab for its header
  // navigation; mark the keys typed here as ours (the event still reaches React).
  useEffect(() => {
    const frame = frameRef.current;
    if (!frame) {
      return undefined;
    }
    const keepKeys = (event: KeyboardEvent) =>
      agGridUtils.stopPropagationForAgGrid(event);
    frame.addEventListener('keydown', keepKeys);
    return () => frame.removeEventListener('keydown', keepKeys);
  }, []);

  const pickers = (
    <Row>
      <Label>{defaults.dateFilterFrom}</Label>
      <ReactDatePicker
        portalId="root"
        dateFormat={DATE_FORMAT}
        inputWidth={DATE_INPUT_WIDTH}
        selectedDate={filter.from}
        maximumDate={filter.to || today}
        onChange={filter.pickFrom}
        ariaLabelledBy={defaults.dateFilterFrom}
      />
      <Label>{defaults.dateFilterTo}</Label>
      <ReactDatePicker
        portalId="root"
        dateFormat={DATE_FORMAT}
        inputWidth={DATE_INPUT_WIDTH}
        selectedDate={filter.to}
        minimumDate={filter.from}
        maximumDate={today}
        onChange={filter.pickTo}
        ariaLabelledBy={defaults.dateFilterTo}
      />
    </Row>
  );
  const buttons = (
    <Row>
      <FilterButton
        variant="contained"
        color="primary"
        size="small"
        onClick={filter.submit}
        data-testid="maia-date-filter-submit"
      >
        {defaults.dateFilterSubmit}
      </FilterButton>
      <ClearButton
        variant="contained"
        size="small"
        onClick={filter.clear}
        data-testid="maia-date-filter-clear"
      >
        {defaults.dateFilterClear}
      </ClearButton>
    </Row>
  );

  return (
    <Frame
      ref={frameRef}
      $stacked={stacked}
      $error={Boolean(filter.error)}
      data-testid="maia-date-filter"
    >
      <CalendarLayer />
      <Legend
        $error={Boolean(filter.error)}
        role={filter.error ? 'alert' : undefined}
        title={filter.error || undefined}
      >
        {filter.error || defaults.dateFilterLegend}
      </Legend>
      {pickers}
      {buttons}
    </Frame>
  );
};

/**
 * For the grid's wrapper. The header row the filter sits in shows no column
 * separators (the hummingbird theme draws them as each header's ::after; `&&`
 * outranks its rule), and the filter fills its group's width: AG Grid lays a
 * group header out as a flex row, which would size the filter to its content.
 * When the filter is wider than its group, it sticks out on the left (flex-end
 * overflows to the start) and draws over the empty group headers there.
 */
export const questionDateFilterGridCss = css`
  && .ag-header-group-cell::after {
    content: none;
  }
  .ag-header-group-cell.${QUESTION_DATE_FILTER_CLASS} {
    z-index: 1;
    padding: 0 4px 4px;
  }
  .ag-header-group-cell.${QUESTION_DATE_FILTER_CLASS} > .ag-react-container {
    flex: 1 1 auto;
    align-self: stretch;
    min-width: 0;
    display: flex;
    justify-content: flex-end;
  }
`;

/** What a grid gives its filter header; null draws nothing (the tile). */
export const QuestionDateFilterContext = createContext<QuestionDateFilterProps | null>(
  null,
);

/**
 * AG Grid header group component: the filter, from the grid's context. AG
 * Grid renders it through a portal inside the grid, so the context reaches it.
 */
export const QuestionDateFilterHeader = () => {
  const props = useContext(QuestionDateFilterContext);
  return props ? <QuestionDateFilter {...props} /> : null;
};

/**
 * Puts the columns from `firstColId` to `lastColId` under one header group
 * that draws the filter. They stay together and cannot be dragged out (a drag
 * on the group would also start on a click in the filter).
 */
export const withQuestionDateFilter = (
  defs: ColDef[],
  firstColId: string,
  lastColId: string,
): (ColDef | ColGroupDef)[] => {
  const first = defs.findIndex((def) => def.colId === firstColId);
  const last = defs.findIndex((def) => def.colId === lastColId);
  return [
    ...defs.slice(0, first),
    {
      groupId: 'questionDates',
      headerName: '',
      headerClass: QUESTION_DATE_FILTER_CLASS,
      headerGroupComponentFramework: QuestionDateFilterHeader,
      marryChildren: true,
      children: defs
        .slice(first, last + 1)
        .map((def) => ({ ...def, suppressMovable: true })),
    },
    ...defs.slice(last + 1),
  ];
};
